import {
  useCallback,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import './BeforeAfterSlider.css';

const MIN_VALUE = 0;
const MAX_VALUE = 100;
const DEFAULT_VALUE = 50;
const SMALL_STEP = 1;
const LARGE_STEP = 10;

export interface BeforeAfterSliderImage {
  /** Image source URL */
  src: string;
  /** Accessible alt text for the image */
  alt: string;
}

export interface BeforeAfterSliderProps {
  /** The "before" image, shown on the left/underneath */
  beforeImage: BeforeAfterSliderImage;
  /** The "after" image, shown on the right/on top, revealed by the divider */
  afterImage: BeforeAfterSliderImage;
  /** Visible label for the before image */
  beforeLabel: string;
  /** Visible label for the after image */
  afterLabel: string;
  /** When true, disables transition/animation for an instant snap (prefers-reduced-motion) */
  reducedMotion: boolean;
  /** Accessible label for the slider control itself */
  ariaLabel: string;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function BeforeAfterSlider({
  beforeImage,
  afterImage,
  beforeLabel,
  afterLabel,
  reducedMotion,
  ariaLabel,
}: BeforeAfterSliderProps) {
  const [value, setValue] = useState<number>(DEFAULT_VALUE);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef<boolean>(false);
  const generatedId = useId();

  const updateValueFromClientX = useCallback((clientX: number) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = (clientX - rect.left) / rect.width;
    const nextValue = clamp(Math.round(ratio * 100), MIN_VALUE, MAX_VALUE);
    setValue(nextValue);
  }, []);

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      draggingRef.current = true;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      updateValueFromClientX(event.clientX);
    },
    [updateValueFromClientX],
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      updateValueFromClientX(event.clientX);
    },
    [updateValueFromClientX],
  );

  const handlePointerUp = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    draggingRef.current = false;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
  }, []);

  const handleKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        event.preventDefault();
        setValue((prev) => clamp(prev - SMALL_STEP, MIN_VALUE, MAX_VALUE));
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        event.preventDefault();
        setValue((prev) => clamp(prev + SMALL_STEP, MIN_VALUE, MAX_VALUE));
        break;
      case 'PageDown':
        event.preventDefault();
        setValue((prev) => clamp(prev - LARGE_STEP, MIN_VALUE, MAX_VALUE));
        break;
      case 'PageUp':
        event.preventDefault();
        setValue((prev) => clamp(prev + LARGE_STEP, MIN_VALUE, MAX_VALUE));
        break;
      case 'Home':
        event.preventDefault();
        setValue(MIN_VALUE);
        break;
      case 'End':
        event.preventDefault();
        setValue(MAX_VALUE);
        break;
      default:
        break;
    }
  }, []);

  const beforeLabelId = `${generatedId}-before-label`;
  const afterLabelId = `${generatedId}-after-label`;

  const afterImageStyle: React.CSSProperties & Record<string, string> = {
    width: `${(100 / (value || 1)) * 100}%`,
  };

  return (
    <div
      className={`rg-before-after-slider${reducedMotion ? '' : ' rg-before-after-slider--animated'}`}
      ref={containerRef}
      data-testid="before-after-slider-track"
      data-reduced-motion={reducedMotion}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <div className="rg-before-after-slider__image-wrapper">
        <img src={beforeImage.src} alt={beforeImage.alt} className="rg-before-after-slider__image" />
      </div>
      <div
        className="rg-before-after-slider__after"
        // Dynamic layout value driven by slider state (not a design decision) —
        // this inline style intentionally expresses the current reveal percentage.
        style={{ width: `${value}%` }}
      >
        <div
          className="rg-before-after-slider__image-wrapper"
          // Dynamic layout value: the after image must be rendered at full container
          // width regardless of the clipped wrapper width, so its width is expressed
          // as an explicit style computed from the current reveal percentage.
          style={afterImageStyle}
        >
          <img src={afterImage.src} alt={afterImage.alt} className="rg-before-after-slider__image" />
        </div>
      </div>
      <div
        className="rg-before-after-slider__divider"
        // Dynamic layout value driven by slider state (not a design decision) —
        // positions the divider line at the current reveal percentage.
        style={{ left: `${value}%` }}
      >
        <div
          className="rg-before-after-slider__handle"
          role="slider"
          tabIndex={0}
          aria-label={ariaLabel}
          aria-valuemin={MIN_VALUE}
          aria-valuemax={MAX_VALUE}
          aria-valuenow={value}
          aria-orientation="horizontal"
          aria-describedby={`${beforeLabelId} ${afterLabelId}`}
          onKeyDown={handleKeyDown}
        >
                  </div>
      </div>
      <div className="rg-before-after-slider__labels">
        <span id={beforeLabelId} className="rg-before-after-slider__label">{beforeLabel}</span>
        <span id={afterLabelId} className="rg-before-after-slider__label">{afterLabel}</span>
      </div>
    </div>
  );
}
